import { useCallback, useEffect, useMemo, useState } from "react";
import { PriceChart } from "../components/PriceChart";
import { TradingViewEmbed } from "../components/TradingViewEmbed";
import { fmtPx, fmtSignedPct } from "../lib/format";
import type { ChartSnapshot } from "../types";

type Props = {
  listChartSymbols: () => Promise<string[]>;
  getChartSnapshot: (
    symbol: string,
    lookback?: number,
  ) => Promise<ChartSnapshot>;
};

type Tab = "native" | "tradingview";

export const ChartScreen = ({
  listChartSymbols,
  getChartSnapshot,
}: Props) => {
  const [symbols, setSymbols] = useState<string[]>([]);
  const [symbol, setSymbol] = useState("NVDA");
  const [draftSymbol, setDraftSymbol] = useState("NVDA");
  const [lookback, setLookback] = useState(260);
  const [tab, setTab] = useState<Tab>("native");
  const [snapshot, setSnapshot] = useState<ChartSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSma, setShowSma] = useState(true);
  const [showRsi, setShowRsi] = useState(true);
  const [showMarkers, setShowMarkers] = useState(true);
  const [showLevels, setShowLevels] = useState(true);
  const [showTable, setShowTable] = useState(false);
  const [fitKey, setFitKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    listChartSymbols()
      .then((rows) => {
        if (cancelled) return;
        setSymbols(rows);
        if (rows.length > 0 && symbol === "NVDA") {
          setSymbol(rows[0]);
          setDraftSymbol(rows[0]);
        }
      })
      .catch(() => {
        if (!cancelled) setSymbols([]);
      });
    return () => {
      cancelled = true;
    };
  }, [listChartSymbols]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getChartSnapshot(symbol, lookback);
      setSnapshot(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Chart load failed");
      setSnapshot(null);
    } finally {
      setLoading(false);
    }
  }, [getChartSnapshot, symbol, lookback]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const tvSymbol = useMemo(() => symbol.toUpperCase(), [symbol]);
  const lastBar = snapshot?.bars.at(-1);
  const firstBar = snapshot?.bars.at(0);
  const movePct =
    firstBar && lastBar && firstBar.close > 0
      ? ((lastBar.close - firstBar.close) / firstBar.close) * 100
      : 0;

  const submitSymbol = () => {
    const clean = draftSymbol.trim().toUpperCase();
    if (clean.length === 0) return;
    setSymbol(clean);
  };

  return (
    <div data-screen-label="Charts">
      <div className="tp-page-head">
        <h1>Charts</h1>
        <div className="meta">
          {snapshot ? `${snapshot.symbol} · ${snapshot.timeframe}` : "daily"}
        </div>
      </div>

      <div className="tp-chart-shell">
        <div className="tp-chart-toolbar">
          <div className="tp-field-inline">
            <label htmlFor="chart-symbol">Symbol</label>
            <input
              id="chart-symbol"
              className="tp-input"
              list="chart-symbols"
              value={draftSymbol}
              onChange={(e) => setDraftSymbol(e.target.value)}
              onBlur={submitSymbol}
              onKeyDown={(e) => {
                if (e.key === "Enter") submitSymbol();
              }}
            />
            <datalist id="chart-symbols">
              {symbols.map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>
          </div>

          <div className="tp-segmented" aria-label="Chart source">
            <button
              type="button"
              className={tab === "native" ? "active" : ""}
              onClick={() => setTab("native")}
            >
              Native
            </button>
            <button
              type="button"
              className={tab === "tradingview" ? "active" : ""}
              onClick={() => setTab("tradingview")}
            >
              TradingView
            </button>
          </div>

          <select
            className="tp-input"
            value={lookback}
            onChange={(e) => setLookback(Number(e.target.value))}
            aria-label="Lookback"
          >
            <option value={90}>90D</option>
            <option value={180}>180D</option>
            <option value={260}>1Y</option>
            <option value={520}>2Y</option>
          </select>

          <button
            type="button"
            className="tp-btn tp-btn-secondary tp-btn-sm"
            onClick={() => setFitKey((k) => k + 1)}
          >
            Fit
          </button>
          <button
            type="button"
            className="tp-btn tp-btn-secondary tp-btn-sm"
            onClick={() => void refresh()}
          >
            Refresh
          </button>
        </div>

        {snapshot && (
          <div className="tp-chart-summary">
            <span className="ticker">{snapshot.symbol}</span>
            {lastBar && <span className="num">{fmtPx(lastBar.close)}</span>}
            <span className={"num " + (movePct >= 0 ? "pos" : "neg")}>
              {fmtSignedPct(movePct)}
            </span>
            <span>{snapshot.markers.length} markers</span>
            <span>{snapshot.trades.length} completed trades</span>
          </div>
        )}

        {tab === "native" && (
          <>
            <div className="tp-chart-controls">
              <label>
                <input
                  type="checkbox"
                  checked={showSma}
                  onChange={(e) => setShowSma(e.target.checked)}
                />
                SMA
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={showMarkers}
                  onChange={(e) => setShowMarkers(e.target.checked)}
                />
                Markers
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={showRsi}
                  onChange={(e) => setShowRsi(e.target.checked)}
                />
                RSI
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={showLevels}
                  onChange={(e) => setShowLevels(e.target.checked)}
                />
                Levels
              </label>
              <button
                type="button"
                className="tp-btn tp-btn-ghost tp-btn-sm"
                onClick={() => setShowTable((v) => !v)}
              >
                {showTable ? "Hide table" : "View as table"}
              </button>
            </div>

            {loading ? (
              <div className="tp-chart-empty">Loading chart...</div>
            ) : error ? (
              <div className="tp-banner bad">
                <span className="icon">!</span>
                <span>{error}</span>
              </div>
            ) : snapshot && snapshot.bars.length > 0 ? (
              <PriceChart
                snapshot={snapshot}
                showSma={showSma}
                showRsi={showRsi}
                showMarkers={showMarkers}
                showLevels={showLevels}
                fitKey={fitKey}
              />
            ) : (
              <div className="tp-chart-empty">No data available.</div>
            )}

            {showTable && snapshot && <ChartDataTable snapshot={snapshot} />}
          </>
        )}

        {tab === "tradingview" && (
          <div className="tp-tv-frame">
            <TradingViewEmbed symbol={tvSymbol} />
          </div>
        )}
      </div>
    </div>
  );
};

const ChartDataTable = ({ snapshot }: { snapshot: ChartSnapshot }) => (
  <div className="tp-tile" style={{ marginTop: 14 }}>
    <div className="tp-tile-head">
      <span className="label">Daily bars</span>
      <span className="tp-muted">{snapshot.bars.length} rows</span>
    </div>
    <div className="tp-tile-body" style={{ padding: 0 }}>
      <table className="tp-table">
        <thead>
          <tr>
            <th>Date</th>
            <th className="right">Open</th>
            <th className="right">High</th>
            <th className="right">Low</th>
            <th className="right">Close</th>
            <th className="right">Volume</th>
          </tr>
        </thead>
        <tbody>
          {snapshot.bars.slice(-80).map((bar) => (
            <tr key={bar.time}>
              <td className="num">{bar.time}</td>
              <td className="num right">{fmtPx(bar.open)}</td>
              <td className="num right">{fmtPx(bar.high)}</td>
              <td className="num right">{fmtPx(bar.low)}</td>
              <td className="num right">{fmtPx(bar.close)}</td>
              <td className="num right">{bar.volume.toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);
